import { Check, CircleAlert, Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

type ResultProps = {
  tone?: 'success' | 'danger' | 'neutral';
  title: ReactNode;
  description?: ReactNode;
  /** 다음 행동 버튼들 */
  actions?: ReactNode;
  className?: string;
};

const TONE_ICON = {
  success: { icon: Check, className: 'bg-fill-success text-fg-inverse' },
  danger: { icon: CircleAlert, className: 'bg-fill-danger-weak text-fg-danger' },
  neutral: { icon: Info, className: 'bg-fill-brand-weak text-fg-brand' },
} as const;

/**
 * TDS result — 성공·실패·빈 상태 종료 화면. 가운데 시맨틱 원형 아이콘 + 타이틀 + 보조 카피 + 다음 행동.
 * 오류 화면도 사용자를 멈춰 세우지 않고 다음 화면으로 안내한다 (Navigating error).
 */
export function Result({ tone = 'neutral', title, description, actions, className }: ResultProps) {
  const { icon: Icon, className: iconClassName } = TONE_ICON[tone];
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className={cn('flex size-14 items-center justify-center rounded-full', iconClassName)}>
        <Icon className='size-7' strokeWidth={tone === 'success' ? 3 : 2} />
      </span>
      <h2 className='mt-5 text-h4 text-fg-primary'>{title}</h2>
      {description ? (
        <p className='mt-2 max-w-sm text-body-2 text-fg-secondary'>{description}</p>
      ) : null}
      {actions ? (
        <div className='mt-8 flex w-full max-w-sm flex-col gap-2 sm:flex-row sm:justify-center [&>*]:flex-1'>
          {actions}
        </div>
      ) : null}
    </div>
  );
}

type EmptyStateProps = {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
};

/** 목록·차트가 비었을 때 — 카드 안에서 쓰는 작은 결과 표시 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-10 text-center', className)}>
      {icon ? (
        <span className='flex size-11 items-center justify-center rounded-l bg-fill-brand-weak text-fg-brand [&_svg]:size-5'>
          {icon}
        </span>
      ) : null}
      <p className='mt-3 text-label-m text-fg-primary'>{title}</p>
      {description ? (
        <p className='mt-1 max-w-sm text-body-3 text-fg-tertiary'>{description}</p>
      ) : null}
      {action ? <div className='mt-4'>{action}</div> : null}
    </div>
  );
}
